import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { ThumbDown as ThumbDownIcon, ThumbUp as ThumbUpIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { get, map } from 'lodash';
import { ILumaBotTemplateItemAnswer } from 'ui-model';

import { useCurrentLanguage } from '@lib/hooks';

import { Button, TypographyI18n } from 'components/v2';

import { LumaBotContextProps } from '../LumaBot.context';
import { EMessageType } from '../WIP/ui-model/types';

import Message, { MessageProps } from './Message';
import { TMessageWrapper } from './MessageWrapper';

const styles = (theme: Theme) =>
  createStyles({
    choicesWrapper: {
      marginTop: theme.spacing.unit * 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      '&$yesNoType': {
        flexDirection: 'row',
        gap: `${theme.spacing.unit}px`,
      },
    },
    button: {
      marginBottom: theme.spacing.unit,
      display: 'grid',
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, 0.8),
      },
      '&$yesNoType': {
        display: 'flex',
        marginBottom: 0,
        minWidth: 'fit-content',
      },
    },
    yesNoType: {},
  });

export interface MessageMultipleChoiceProps extends Omit<MessageProps, 'classes'>, WithStyles<typeof styles> {
  message: TMessageWrapper;
  onSubmit: LumaBotContextProps['pushAnswer'];
}

const MessageMultipleChoice: React.FC<MessageMultipleChoiceProps> = ({ classes, message, onSubmit, ...rest }) => {
  const type = get(message.metadata.item, 'type');
  const language = useCurrentLanguage();
  const isEnglish = language === 'en';
  const isTypeYesNo = type === 'yes_no';
  const allAnswers: ILumaBotTemplateItemAnswer[] = get(message.metadata.item, 'properties.answers');

  return (
    <Message message={message} {...rest}>
      {/* Display answers */}
      {message.metadata.isLastMessage && allAnswers.length > 0 && (
        <div className={classnames(classes.choicesWrapper, { [classes.yesNoType]: isTypeYesNo })}>
          {map(allAnswers, (answer, index) => {
            let icon;
            if (isTypeYesNo && index === 0) icon = <ThumbUpIcon fontSize="small" />;
            if (isTypeYesNo && index === 1) icon = <ThumbDownIcon fontSize="small" />;
            return (
              <Button
                data-lh-id={`button-${answer.id}`}
                key={answer.id}
                className={classnames(classes.button, { [classes.yesNoType]: isTypeYesNo })}
                variant="outlined"
                extraPadding
                extraRounded
                fullWidth
                icon={icon}
                onClick={() => {
                  onSubmit({
                    text: get(answer, 'label', ''),
                    type: EMessageType.Outbound,
                    metadata: { ...message.metadata, answer },
                  });
                }}
              >
                <TypographyI18n variant="bW600F14" color="inherit" noWrap>
                  {isEnglish ? answer.label : get(answer, `locales.${language}.label`) || answer.label}
                </TypographyI18n>
              </Button>
            );
          })}
        </div>
      )}
    </Message>
  );
};

export default withStyles(styles, { name: 'MessageMultipleChoice' })(MessageMultipleChoice);
